import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { canShowExchangeMessage } from '../../store/actions/socketMessage';
import { incrementNotificationBadge } from '../../store/actions/notificationBadge';

// Same exchanges the bell page shows
const BELL_EXCHANGES = ['MCX', 'NFO', 'CDS', 'NSE', 'ALL'];
const NOTIFIED_KEYS_STORAGE = 'notifiedExchangeNotifications';
const MAX_NOTIFIED_KEYS = 15;
// Frames of one batch can briefly replace each other in a slot, so check once the slots settle
const SETTLE_DELAY_MS = 500;

const getNotificationKey = (exchange, message) => [
  exchange,
  message.currentSession?.[0]?.sessionTime ?? '',
  message.genericMessage || message.bodMessage || message.holidayMessage,
].join('|');

const readNotifiedKeys = () => {
  try {
    const keys = JSON.parse(sessionStorage.getItem(NOTIFIED_KEYS_STORAGE));
    return Array.isArray(keys) ? keys : [];
  } catch (_e) {
    return [];
  }
};

const saveNotifiedKeys = (keys) => {
  try {
    sessionStorage.setItem(NOTIFIED_KEYS_STORAGE, JSON.stringify(keys.slice(-MAX_NOTIFIED_KEYS)));
  } catch (_e) {
    // storage unavailable: a notification may toast again after a refresh
  }
};

// Shows a 2s toast and bumps the bell badge once per new exchange notification. Notified keys are kept
// in sessionStorage so repeated batches and page refreshes in this tab don't notify the same message again.
function useExchangeNotificationAlerts() {
  const exchangeMessages = useSelector((state) => state.socketMessage?.exchangeMessages);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!exchangeMessages) return undefined;

    const timer = setTimeout(() => {
      const notifiedKeys = readNotifiedKeys();
      const newKeys = [];

      BELL_EXCHANGES.forEach((exchange) => {
        const message = exchangeMessages[exchange]?.currentMessage;
        if (!canShowExchangeMessage(message)) return;

        const key = getNotificationKey(exchange, message);
        if (notifiedKeys.includes(key) || newKeys.includes(key)) return;

        newKeys.push(key);
        dispatch(incrementNotificationBadge());
        enqueueSnackbar(`New exchange update — ${exchange}`, {
          variant: 'info',
          autoHideDuration: 2000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          SnackbarProps: { onClick: () => navigate('/notifications'), style: { cursor: 'pointer' } },
        });
      });

      if (newKeys.length) saveNotifiedKeys([...notifiedKeys, ...newKeys]);
    }, SETTLE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [exchangeMessages, dispatch, navigate]);
}

export default useExchangeNotificationAlerts;
